import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";

const GOALS = [
    { value: "first_time_buyer", label: "First-time buyer" },
    { value: "moving_home", label: "Moving home" },
    { value: "remortgage", label: "Remortgaging" },
    { value: "buy_to_let", label: "Buy-to-let / landlord" },
    { value: "commercial", label: "Commercial / business finance" },
    { value: "protection", label: "Protection & insurance" },
];

const TIMELINES = [
    { value: "asap", label: "As soon as possible" },
    { value: "1_3_months", label: "Within 1–3 months" },
    { value: "3_6_months", label: "3–6 months" },
    { value: "exploring", label: "Just exploring" },
];

export default function LeadWizard() {
    const [step, setStep] = useState(0);
    const [data, setData] = useState({
        goal: "",
        property_value: "",
        deposit: "",
        income: "",
        timeline: "",
        name: "",
        email: "",
        phone: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const steps = [
        { id: "goal", label: "Your goal" },
        { id: "numbers", label: "Numbers" },
        { id: "timeline", label: "Timeline" },
        { id: "details", label: "Your details" },
    ];

    const canNext = () => {
        if (step === 0) return !!data.goal;
        if (step === 1) return !!data.property_value && !!data.deposit;
        if (step === 2) return !!data.timeline;
        if (step === 3) return !!data.name && !!data.email && !!data.phone;
        return false;
    };

    const CHECKMYFILE_URL = "https://www.checkmyfile.partners/H4CRD56/2CTPL/";

    const submit = async () => {
        setSubmitting(true);
        try {
            await createLead({
                name: data.name,
                email: data.email,
                phone: data.phone || null,
                goal: data.goal,
                property_value: data.property_value
                    ? parseFloat(data.property_value)
                    : null,
                deposit: data.deposit ? parseFloat(data.deposit) : null,
                income: data.income ? parseFloat(data.income) : null,
                timeline: data.timeline,
                service: data.goal,
                source: "home_wizard",
                message: `Wizard submission: ${data.goal} · ${data.timeline} · Credit-check handoff to checkmyfile`,
            });
            toast.success(
                "Thanks — opening your free credit report now."
            );
            // Open partner credit-report page in a new tab so the lead stays in admin
            window.open(CHECKMYFILE_URL, "_blank", "noopener,noreferrer");
            navigate("/thank-you");
        } catch (err) {
            console.error(err);
            toast.error("Submission failed. Please try again or call us.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="lead-wizard-section"
            id="lead-wizard-section"
            className="relative py-14 md:py-20"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-semibold">
                                Qualify in 60 seconds
                            </span>
                        </div>
                        <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-primary leading-[1.05]">
                            Tell us what
                            <br />
                            you're hoping to
                            <br />
                            <em className="italic font-light">achieve.</em>
                        </h2>
                        <p className="mt-8 text-lg text-brand-muted leading-relaxed max-w-md">
                            Four short steps to help us understand your situation. An
                            adviser will review your answers and be in touch to discuss
                            your options.
                        </p>

                        <div className="mt-10 space-y-3">
                            {steps.map((s, i) => (
                                <div
                                    key={s.id}
                                    className={`flex items-center gap-4 text-sm ${
                                        i <= step ? "text-brand-primary" : "text-brand-muted"
                                    }`}
                                >
                                    <span
                                        className={`h-7 w-7 rounded-full flex items-center justify-center text-xs ${
                                            i < step
                                                ? "bg-brand-primary text-brand-bg"
                                                : i === step
                                                    ? "border border-brand-primary text-brand-primary"
                                                    : "border border-brand-border text-brand-muted"
                                        }`}
                                    >
                                        {i < step ? <Check size={12} /> : i + 1}
                                    </span>
                                    <span className="tracking-wide">{s.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-7 bg-brand-surface border border-brand-border p-8 md:p-12">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {step === 0 && (
                                    <div data-testid="wizard-step-goal">
                                        <h3 className="font-serif-editorial text-3xl text-brand-primary mb-8">
                                            What brings you to us today?
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {GOALS.map((g) => (
                                                <button
                                                    key={g.value}
                                                    data-testid={`wizard-goal-${g.value}`}
                                                    onClick={() => update("goal", g.value)}
                                                    className={`text-left p-5 border transition-all ${
                                                        data.goal === g.value
                                                            ? "border-brand-primary bg-brand-primary/5"
                                                            : "border-brand-border hover:border-brand-primary/50"
                                                    }`}
                                                >
                                                    <div className="font-medium text-brand-primary">
                                                        {g.label}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {step === 1 && (
                                    <div data-testid="wizard-step-numbers">
                                        <h3 className="font-serif-editorial text-3xl text-brand-primary mb-8">
                                            Rough figures
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <NumberField
                                                label="Property value (£)"
                                                value={data.property_value}
                                                onChange={(v) => update("property_value", v)}
                                                placeholder="450000"
                                                testid="wizard-property-value"
                                            />
                                            <NumberField
                                                label="Deposit / equity (£)"
                                                value={data.deposit}
                                                onChange={(v) => update("deposit", v)}
                                                placeholder="90000"
                                                testid="wizard-deposit"
                                            />
                                            <NumberField
                                                label="Annual income (£, optional)"
                                                value={data.income}
                                                onChange={(v) => update("income", v)}
                                                placeholder="55000"
                                                testid="wizard-income"
                                            />
                                        </div>
                                    </div>
                                )}

                                {step === 2 && (
                                    <div data-testid="wizard-step-timeline">
                                        <h3 className="font-serif-editorial text-3xl text-brand-primary mb-8">
                                            When are you looking to move?
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {TIMELINES.map((t) => (
                                                <button
                                                    key={t.value}
                                                    data-testid={`wizard-timeline-${t.value}`}
                                                    onClick={() => update("timeline", t.value)}
                                                    className={`text-left p-5 border transition-all ${
                                                        data.timeline === t.value
                                                            ? "border-brand-primary bg-brand-primary/5"
                                                            : "border-brand-border hover:border-brand-primary/50"
                                                    }`}
                                                >
                                                    <div className="font-medium text-brand-primary">
                                                        {t.label}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {step === 3 && (
                                    <div data-testid="wizard-step-details">
                                        <h3 className="font-serif-editorial text-3xl text-brand-primary mb-2">
                                            Where shall we send your match?
                                        </h3>
                                        <p className="text-sm text-brand-muted mb-6">
                                            We only use your details to respond to your
                                            enquiry. No marketing spam.
                                        </p>
                                        <div className="rounded-lg border border-brand-border bg-brand-bg/60 p-4 mb-8 text-sm text-brand-primary">
                                            Next step — we'll open your{" "}
                                            <span className="font-bold">free checkmyfile credit report</span>{" "}
                                            in a new tab so an adviser can match you to the
                                            right lender. Your enquiry is saved with us either way.
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <TextField
                                                label="Full name"
                                                value={data.name}
                                                onChange={(v) => update("name", v)}
                                                testid="wizard-name"
                                            />
                                            <TextField
                                                label="Email"
                                                type="email"
                                                value={data.email}
                                                onChange={(v) => update("email", v)}
                                                testid="wizard-email"
                                            />
                                            <TextField
                                                label="Phone"
                                                value={data.phone}
                                                onChange={(v) => update("phone", v)}
                                                testid="wizard-phone"
                                            />
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>

                        <div className="mt-10 flex items-center justify-between">
                            <button
                                data-testid="wizard-back"
                                onClick={() => setStep((s) => Math.max(0, s - 1))}
                                disabled={step === 0}
                                className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-primary transition-colors disabled:opacity-40"
                            >
                                <ArrowLeft size={16} /> Back
                            </button>
                            {step < steps.length - 1 ? (
                                <button
                                    data-testid="wizard-next"
                                    onClick={() => setStep((s) => s + 1)}
                                    disabled={!canNext()}
                                    className="inline-flex items-center gap-2 bg-brand-primary text-brand-bg px-7 py-3.5 text-sm font-medium hover:bg-brand-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Continue <ArrowRight size={16} />
                                </button>
                            ) : (
                                <button
                                    data-testid="wizard-submit"
                                    onClick={submit}
                                    disabled={!canNext() || submitting}
                                    className="inline-flex items-center gap-2 bg-brand-primary text-white px-7 py-3.5 rounded-full text-sm font-bold hover:bg-brand-primaryHover transition-colors disabled:opacity-50"
                                >
                                    {submitting ? "Sending…" : "Get my credit report"}
                                    <ArrowRight size={16} />
                                </button>
                            )}
                        </div>
                        {step === steps.length - 1 && (
                            <p className="mt-4 text-[11px] text-brand-muted leading-relaxed">
                                By submitting your details, you confirm you have
                                read and understood our{" "}
                                <a
                                    href="/privacy"
                                    className="underline text-brand-primary hover:text-brand-accent"
                                >
                                    Privacy Policy
                                </a>
                                .
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function NumberField({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                {label}
            </label>
            <input
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="mt-2 w-full bg-transparent border-b border-brand-border py-3 text-lg text-brand-primary focus:border-brand-primary outline-none transition-colors"
            />
        </div>
    );
}

function TextField({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="mt-2 w-full bg-transparent border-b border-brand-border py-3 text-lg text-brand-primary focus:border-brand-primary outline-none transition-colors"
            />
        </div>
    );
}
